import { useState, useCallback } from "react";
import baseAxios from "axios";
import { Urls } from "../Urls";
import { STORAGE_ACCOUNT } from "../config/env.config";
import axios from "../Axios";

const AZURE_CONTAINER_NAME = "public-container";

export function useAzurePublicUpload() {
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Reserves a signed URL from backend for file upload.
     */
    const reserveAzureSignedUrl = useCallback(async (filename) => {
        const reservePayload = {
            action: "upload",
            fileName: filename,
            accountName: STORAGE_ACCOUNT,
            containerName: AZURE_CONTAINER_NAME,
        };

        const { data } = await axios.post(Urls.lorriFileService, reservePayload);
        const signedUrl = data?.url;
        const fileName = data?.file_name;

        if (!signedUrl || typeof signedUrl !== "string") {
            throw new Error("Invalid upload URL received from backend.");
        }

        return { signedUrl, fileName };
    }, []);

    /**
     * Uploads file to Azure Blob using the signed URL.
     */
    const putFileToAzureBlob = useCallback(async (file, signedUrl) => {
        await baseAxios.put(signedUrl, file, {
            headers: {
                "x-ms-blob-type": "BlockBlob",
                "x-ms-blob-content-disposition": `attachment; filename="${file.name}"`,
            },
        });

        return signedUrl.split("?")[0];  // Strip query params to get the blob URL
    }, []);

    /**
     * High-level upload: reserves signed URL, uploads the file, and returns the final blob URL.
     */
    const uploadFile = useCallback(
        async (file, customFilename = null) => {
            setIsUploading(true);
            setError(null);

            try {
                // Step 1: Reserve a signed URL for the file upload
                const { signedUrl, fileName } = await reserveAzureSignedUrl(customFilename || file.name);

                // Step 2: Upload the file to Azure Blob using the signed URL
                const blobUrl = await putFileToAzureBlob(file, signedUrl);

                return { blobUrl, fileName };
            } catch (err) {
                setError(err.message || "Upload failed");
                throw err;
            } finally {
                setIsUploading(false);
            }
        },
        [reserveAzureSignedUrl, putFileToAzureBlob]
    );

    return { uploadFile, isUploading, error };
}